import React from 'react';
import { motion } from 'framer-motion';
import { 
  CpuChipIcon,
  ClockIcon,
  ChartBarIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const AIEvolution: React.FC = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
          >
            Your Implementation Journey
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Start with one solution, expand as you see results. Our tools are designed to deliver value quickly.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Built for Real Business Impact</h3>
            <p className="text-gray-600 mb-8">
              Our solutions are designed to integrate with your existing workflows and deliver measurable results from day one.
            </p>

            <ul className="space-y-4">
              {[
                {
                  title: "Quick Time-to-Value",
                  description: "Pre-built solutions that work out of the box, not months of customization"
                },
                {
                  title: "Deep Integration",
                  description: "Connects directly to your databases, documents, and business systems"
                },
                {
                  title: "Scalable Architecture",
                  description: "Start with one use case, expand across your organization"
                },
                {
                  title: "Flexible Deployment",
                  description: "Cloud, on-premise, or hybrid - choose what fits your security needs"
                }
              ].map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-2xl mt-1">🧠</span>
                  <div>
                    <strong className="text-gray-900">{benefit.title}:</strong>
                    <span className="text-gray-600 ml-2">{benefit.description}</span>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Timeline */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <div className="space-y-8">
              {[
                {
                  period: "Week 1",
                  title: "Discovery",
                  description: "We assess your needs and configure the right solution",
                  icon: "🔍"
                },
                {
                  period: "Week 2-3",
                  title: "Setup",
                  description: "Connect to your systems and deploy the solution",
                  icon: "🔧"
                },
                {
                  period: "Week 4",
                  title: "Training",
                  description: "Your team learns to use the tools effectively",
                  icon: "📚"
                },
                {
                  period: "Ongoing",
                  title: "Support & Scale",
                  description: "Continuous support and expand to new use cases",
                  icon: "🚀"
                }
              ].map((phase, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-800 to-secondary-800 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 mt-1">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{phase.icon}</span>
                      <div>
                        <div className="font-bold text-gray-900">{phase.period}: {phase.title}</div>
                        <div className="text-sm text-gray-600">{phase.description}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Use Cases Grid */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Popular Use Cases</h3>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Document Processing */}
            <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-8">
              <h4 className="text-xl font-bold text-purple-800 mb-6 flex items-center gap-2">
                <span className="text-2xl">📄</span>
                Document Processing
              </h4>
              <ul className="space-y-4">
                {[
                  "Invoice data extraction and validation",
                  "Contract analysis and deadline tracking",
                  "Automated document classification",
                  "Multi-language document processing",
                  "Compliance document review",
                  "Receipt and expense processing"
                ].map((useCase, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <span className="text-gray-700">{useCase}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Business Intelligence */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-8">
              <h4 className="text-xl font-bold text-blue-800 mb-6 flex items-center gap-2">
                <span className="text-2xl">📊</span>
                Business Intelligence
              </h4>
              <ul className="space-y-4">
                {[
                  "Natural language database queries",
                  "Automated report generation",
                  "Real-time dashboard updates",
                  "Cross-system data analysis",
                  "Executive summary generation",
                  "Trend and anomaly detection"
                ].map((useCase, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-blue-500 mt-1">✓</span>
                    <span className="text-gray-700">{useCase}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Benefits Grid */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: "⚡",
                title: "Fast Deployment",
                description: "Go live in days, not months of development"
              },
              {
                icon: "🔗",
                title: "System Integration",
                description: "Connects to your existing databases and tools"
              },
              {
                icon: "🔒",
                title: "Data Privacy Option",
                description: "On-premise deployment for sensitive industries"
              },
              {
                icon: "📞",
                title: "Expert Support",
                description: "Dedicated team to help you succeed"
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-3xl mb-4">{benefit.icon}</div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h4>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AIEvolution; 